const AUTH_QUERY = /^(x-amz-|x-goog-)/i;

export function signedImageInfo(value) {
  if (typeof value !== 'string') return null;
  let url;
  try { url = new URL(value); } catch { return null; }
  if (!/^https?:$/.test(url.protocol)) return null;
  const params = new Map([...url.searchParams].map(([key, v]) => [key.toLowerCase(), v]));
  const prefix = params.has('x-amz-signature') ? 'x-amz-' : 'x-goog-';
  if (!params.has(`${prefix}signature`)) return null;
  const date = params.get(`${prefix}date`)?.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/);
  const seconds = Number(params.get(`${prefix}expires`));
  if (!date || !Number.isFinite(seconds) || seconds <= 0) return null;
  const signedAt = Date.UTC(+date[1], +date[2] - 1, +date[3], +date[4], +date[5], +date[6]);
  for (const key of [...url.searchParams.keys()]) {
    if (AUTH_QUERY.test(key)) url.searchParams.delete(key);
  }
  url.searchParams.sort();
  url.hash = '';
  return { key: url.href, signedAt, expiresAt: signedAt + seconds * 1000, pathname: url.pathname };
}

export function imageRefreshSource(info, context = {}) {
  const project = info.pathname.match(/\/vision\/projects\/([^/]+)\/([^/]+)\//);
  if (project) {
    const [, projectId, folder] = project;
    if (folder === 'refs') return { kind: 'references', projectId };
    if (folder === 'set-dressing-refs') return { kind: 'setDressing', projectId };
    if (folder === 'generations' && context.generationId && context.projectId === projectId) {
      return context.imageId
        ? { kind: 'generations', projectId, imageId: context.imageId }
        : { kind: 'board', projectId };
    }
    if (['originals', 'thumbnails', 'generations'].includes(folder)) return { kind: 'images', projectId };
  }
  if (/\/vision\/accounts\/[^/]+\/color-presets\//.test(info.pathname)) return { kind: 'colors' };
  return null;
}

export function collectSignedImages(value, context = {}, result = []) {
  if (!value || typeof value !== 'object') return result;
  if (value.generationId && value.projectId) context = value;
  for (const child of Object.values(value)) {
    if (typeof child === 'string') {
      const info = signedImageInfo(child);
      if (!info) continue;
      const source = imageRefreshSource(info, context);
      if (!source) continue;
      const mutableAsset = value.assetId && /\/(originals|thumbnails)\//.test(info.pathname);
      const version = mutableAsset && value.updatedAt
        ? `${value.updatedAt.seconds}:${value.updatedAt.nanos || 0}` : '';
      result.push({ ...info, url: child, source, version });
    } else if (child && typeof child === 'object') {
      collectSignedImages(child, context, result);
    }
  }
  return result;
}

export async function refreshImageSource(source, shouldContinue) {
  switch (source.kind) {
    case 'images': {
      const api = await import('./visionAssetsClient.js');
      return api.listProjectImages(source.projectId, { shouldContinue });
    }
    case 'references': {
      const api = await import('./visionAssetsClient.js');
      return api.listReferenceBank(source.projectId, { shouldContinue });
    }
    case 'setDressing': {
      const api = await import('./visionAssetsClient.js');
      return api.listSetDressingBank(source.projectId, { shouldContinue });
    }
    case 'colors': {
      const api = await import('./visionAssetsClient.js');
      return api.listColorPresets({ shouldContinue });
    }
    case 'generations': {
      const api = await import('./visionGenerationsClient.js');
      return api.listGenerationsByImage(source.projectId, source.imageId, { shouldContinue });
    }
    case 'board': {
      const api = await import('./visionGenerationsClient.js');
      return api.listVisionBoardGenerations({ projectId: source.projectId, shouldContinue });
    }
    default: throw new Error('No image URL renewal source');
  }
}
